import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent, type JSX } from 'react';
import { useAuthStore } from '../auth/authStore';
import { makeApi } from '../../lib/api';
import type { AssetEntry } from '@peacock/sdk-js';
import { AssetThumb } from './AssetThumb';

/**
 * Phase 3 F — Asset Library.
 *
 * Renders a grid of every asset in the current Space, with:
 *  - Upload via file-picker (Phase 7 will add drag-and-drop into the
 *    grid; for Phase 3 a button is enough to satisfy the spec)
 *  - Per-locale alt-text editing on the asset detail panel
 *  - Focal-point picker via a click on the preview image
 *
 * The actual image rendering uses a relative `/v1/cdn/...` URL today,
 * but that endpoint is for Story content, not raw asset bytes. A
 * proper `GET /v1/cdn/{space}/assets/{uuid}` route lands in Phase 4
 * once Glide transforms are wired; until then the grid renders a
 * generic file-icon for non-image MIMEs and a "preview disabled"
 * note for images. This is honest about what works.
 */
export function AssetLibrary(): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const assetsQuery = useQuery({
    enabled: token !== null && spaceSlug !== null,
    queryKey: ['assets', spaceSlug],
    queryFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('queryFn ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.listAssets(spaceSlug, { perPage: 100 });
      if (result.status !== 'ok') {
        throw new Error(`listAssets failed: ${result.status}`);
      }
      return result.data;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (token === null || spaceSlug === null) {
        throw new Error('upload ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.uploadAsset(spaceSlug, file, file.name);
      if (result.status !== 'ok') {
        throw new Error(`uploadAsset failed: ${result.status}`);
      }
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', spaceSlug] });
    },
  });

  const patchMutation = useMutation({
    mutationFn: async (vars: {
      uuid: string;
      payload: { alt?: Record<string, string>; focal_x?: number; focal_y?: number };
    }) => {
      if (token === null || spaceSlug === null) {
        throw new Error('patch ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.patchAsset(spaceSlug, vars.uuid, vars.payload);
      if (result.status !== 'ok') {
        throw new Error(`patchAsset failed: ${result.status}`);
      }
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', spaceSlug] });
    },
  });

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    uploadMutation.mutate(file);
    // Reset the input so re-selecting the same file fires onChange.
    event.target.value = '';
  }

  const assets = assetsQuery.data?.data ?? [];
  const selectedAsset = assets.find((a) => a.uuid === selectedUuid) ?? null;

  return (
    <div className="grid h-full grid-cols-[1fr_320px] gap-4">
      <section className="flex flex-col gap-4 overflow-y-auto">
        <header className="flex items-center justify-between">
          <h1 className="text-lg font-semibold tracking-tight text-zinc-900">
            Assets
          </h1>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">
              {assetsQuery.data?.meta.total ?? '—'} total
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif,image/gif,video/mp4,video/webm,application/pdf"
              onChange={handleFileChange}
              className="hidden"
              data-testid="asset-upload-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="rounded-md bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </header>

        {uploadMutation.isError ? (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            {(uploadMutation.error as Error).message}
          </p>
        ) : null}

        {assetsQuery.isPending ? (
          <p className="text-sm text-zinc-500">Lade Assets…</p>
        ) : assetsQuery.isError ? (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            {(assetsQuery.error as Error).message}
          </p>
        ) : assets.length === 0 ? (
          <p className="text-sm text-zinc-500">No assets yet. Upload one to get started.</p>
        ) : (
          <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
            {assets.map((asset) => (
              <AssetCard
                key={asset.uuid}
                asset={asset}
                selected={asset.uuid === selectedUuid}
                onClick={() => setSelectedUuid(asset.uuid)}
              />
            ))}
          </ul>
        )}
      </section>

      <aside className="overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50 p-3">
        {selectedAsset === null ? (
          <p className="text-xs text-zinc-500">Select an asset to edit alt text and focal point.</p>
        ) : (
          <AssetDetail
            asset={selectedAsset}
            onPatch={(payload) =>
              patchMutation.mutate({ uuid: selectedAsset.uuid, payload })
            }
            isPatching={patchMutation.isPending}
            patchError={patchMutation.error as Error | null}
          />
        )}
      </aside>
    </div>
  );
}

function AssetCard({
  asset,
  selected,
  onClick,
}: {
  asset: AssetEntry;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={
          'flex w-full flex-col gap-1 rounded-md border p-2 text-left transition ' +
          (selected
            ? 'border-zinc-900 bg-white shadow-sm'
            : 'border-zinc-200 bg-white hover:border-zinc-400')
        }
      >
        {/* Real thumbnail preview (2026-06-11) — was a grey MIME
            placeholder ("Phase 4 pending"); the backend has shipped
            signed thumb_url since the Live-Builder asset picker, so we
            render it now. */}
        <AssetThumb asset={asset} />
        <span className="truncate text-xs font-medium text-zinc-900">
          {asset.original_filename}
        </span>
        <span className="text-[10px] font-mono text-zinc-500">
          {formatBytes(asset.size_bytes)}
        </span>
      </button>
    </li>
  );
}

function AssetDetail({
  asset,
  onPatch,
  isPatching,
  patchError,
}: {
  asset: AssetEntry;
  onPatch: (payload: { alt?: Record<string, string>; focal_x?: number; focal_y?: number }) => void;
  isPatching: boolean;
  patchError: Error | null;
}): JSX.Element {
  // Locale set: union of the locales currently in alt + a `de-AT`
  // fallback so the editor can always populate at least one. Phase
  // 3 G's locale switcher will replace this hardcoded set with the
  // Space's actual locales.
  const knownLocales = new Set<string>(['de-AT', 'en-GB', ...Object.keys(asset.alt)]);

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h2 className="truncate text-sm font-medium text-zinc-900" title={asset.original_filename}>
          {asset.original_filename}
        </h2>
        <p className="mt-1 font-mono text-[10px] uppercase text-zinc-500">
          {asset.mime} · {formatBytes(asset.size_bytes)}
        </p>
      </header>

      <section>
        <h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500">
          Alt text per locale
        </h3>
        <div className="mt-2 flex flex-col gap-2">
          {[...knownLocales].map((code) => (
            <label key={code} className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase text-zinc-500">{code}</span>
              <input
                type="text"
                defaultValue={asset.alt[code] ?? ''}
                onBlur={(e) => {
                  const next = e.target.value;
                  if (next === (asset.alt[code] ?? '')) return;
                  onPatch({ alt: { ...asset.alt, [code]: next } });
                }}
                className="rounded border border-zinc-300 px-2 py-1 text-xs focus:border-zinc-900 focus:outline-none"
                data-testid={`alt-input-${code}`}
              />
            </label>
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500">
          Focal point (0–1)
        </h3>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] text-zinc-500">x</span>
            <input
              type="number"
              step="0.05"
              min="0"
              max="1"
              defaultValue={asset.focal_x}
              onBlur={(e) => {
                const next = Number(e.target.value);
                if (Number.isFinite(next) && next !== asset.focal_x) {
                  onPatch({ focal_x: next });
                }
              }}
              className="rounded border border-zinc-300 px-2 py-1 text-xs"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] text-zinc-500">y</span>
            <input
              type="number"
              step="0.05"
              min="0"
              max="1"
              defaultValue={asset.focal_y}
              onBlur={(e) => {
                const next = Number(e.target.value);
                if (Number.isFinite(next) && next !== asset.focal_y) {
                  onPatch({ focal_y: next });
                }
              }}
              className="rounded border border-zinc-300 px-2 py-1 text-xs"
            />
          </label>
        </div>
      </section>

      {isPatching ? (
        <p className="text-xs text-zinc-500">Saving…</p>
      ) : null}
      {patchError !== null ? (
        <p role="alert" className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-900">
          {patchError.message}
        </p>
      ) : null}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
